// Vue & Plugins
import { MaybeRefOrGetter, computed, toValue } from 'vue'

// Composables
import {
  keepPreviousData,
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/vue-query'

import { type SiteID } from '../../sites/models/site'
// Models
import {
  NOTIFICATION_TYPE_GROUPS,
  NotificationID,
  NotificationReadFilter,
  NotificationTypeGroup,
} from '../models/notification'

// Stores
import { useEventStore } from '../../common/eventStore'

// Services
import {
  NotificationSearchForm,
  fetch,
  read,
  readAll,
} from '../notificationService'

interface NotificationFilters {
  perPage?: MaybeRefOrGetter<number>
  page?: MaybeRefOrGetter<number>
  filter?: MaybeRefOrGetter<NotificationReadFilter>
  typeGroup?: MaybeRefOrGetter<NotificationTypeGroup>
}

export const notificationKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationKeys.all, 'list'] as const,
  list: (filters: NotificationFilters) =>
    [
      ...notificationKeys.lists(),
      {
        perPage: filters.perPage,
        page: filters.page,
        filter: filters.filter,
        typeGroup: filters.typeGroup,
      },
    ] as const,
}

export function useNotificationQuery(
  siteId: MaybeRefOrGetter<SiteID | null>,
  filters: NotificationFilters = {},
) {
  const queryFn = computed(() => {
    const form: NotificationSearchForm = {}

    if (filters.perPage) {
      form.perPage = toValue(filters.perPage)
    }

    if (filters.page) {
      form.page = toValue(filters.page)
    }

    if (filters.filter) {
      form.filter = toValue(filters.filter)
    }

    if (filters.typeGroup) {
      form.types = [...NOTIFICATION_TYPE_GROUPS[toValue(filters.typeGroup)]]
    }

    const id = toValue(siteId)
    return id ? () => fetch(id, form) : skipToken
  })
  return useQuery({
    queryKey: notificationKeys.list(filters),
    queryFn: queryFn,
    placeholderData: keepPreviousData,
  })
}

/**
 * The unread total for the badge in the header and on the dashboard. It is
 * kept apart from the list query so the user's list filters cannot change it.
 */
export function useUnreadNotificationCountQuery(
  siteId: MaybeRefOrGetter<SiteID | null>,
) {
  const query = useNotificationQuery(siteId, {
    filter: 'unread',
    perPage: 1,
    page: 1,
  })

  return {
    ...query,
    unreadTotal: computed(() => query.data.value?.pagination.total ?? 0),
  }
}

export function useNotificationReadAllMutation(
  siteId: MaybeRefOrGetter<SiteID>,
) {
  const queryClient = useQueryClient()
  const eventStore = useEventStore()

  return useMutation<void, Error>({
    mutationFn: async () => {
      return readAll(toValue(siteId))
    },
    onSettled: async () => {
      return await queryClient.invalidateQueries({
        queryKey: notificationKeys.lists(),
      })
    },
    onError: async (error) => {
      eventStore.addToast({
        severity: 'error',
        summary: 'Error',
        detail: error.message,
      })
    },
  })
}

export function useNotificationReadMutation(siteId: MaybeRefOrGetter<SiteID>) {
  const queryClient = useQueryClient()
  const eventStore = useEventStore()

  return useMutation<void, Error, { notificationId: NotificationID }>({
    mutationFn: async (data) => {
      return read(toValue(siteId), data.notificationId)
    },
    onSettled: async () => {
      return await queryClient.invalidateQueries({
        queryKey: notificationKeys.lists(),
      })
    },
    onError: async (error) => {
      eventStore.addToast({
        severity: 'error',
        summary: 'Error',
        detail: error.message,
      })
    },
  })
}
